import React, { useEffect } from "react";

import SelectAutoComplete from "./form/SelectAutoComplete";
import SwitchField from "./form/Switch";
import { Field, FormSpy, useForm } from "react-final-form";
import Grid from "@mui/material/Grid";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import FormHelperText from "@mui/material/FormHelperText";

import { mapToDisplay } from "../CameraNames";

// Effect-only helper: when the camera union shrinks (e.g. a vehicle is
// removed), drop any ticked cameraPositions no longer in the union so a
// stale pre-ticked box is never shown or submitted.
const PruneStaleCameraPositions = ({ union }) => {
  const form = useForm();
  const unionKey = union.join("|");

  useEffect(() => {
    const current = form.getState().values.cameraPositions || [];
    const allowed = unionKey ? unionKey.split("|") : [];
    const pruned = current.filter((p) => allowed.includes(p));
    if (pruned.length !== current.length) {
      form.change("cameraPositions", pruned);
    }
  }, [unionKey, form]);

  return null;
};

const CreateLiveShareTrackingForm = ({
  handleChange,
  getOptionLabel,
  options,
  classes,
  defaultDateStart,
  defaultDateEnd,
  selectedVehicles,
}) => {
  // Distinct union of camera positions across the selected vehicles, built in selection order:
  // walk each vehicle's cameras, append any position not seen yet, skip duplicates. Location is the
  // human-meaningful, cross-vehicle-comparable camera name; the backend maps each picked position
  // to the actual channel on each vehicle.
  const cameraUnion = (() => {
    const seen = new Set();
    const list = [];
    (selectedVehicles || []).forEach((v) =>
      (v.cameras || []).forEach((c) => {
        if (!seen.has(c.location)) {
          seen.add(c.location);
          list.push(c.location);
        }
      }),
    );
    return list;
  })();

  return (
    <div>
      <PruneStaleCameraPositions union={cameraUnion} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <div className={classes.itemContainer}>
              <Field name="accessStart">
                {({ input }) => (
                  <DateTimePicker
                    label="Access Start"
                    value={defaultDateStart}
                    onChange={input.onChange}
                  />
                )}
              </Field>
            </div>
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={12}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <div className={classes.itemContainer}>
              <Field name="accessEnd">
                {({ input }) => (
                  <DateTimePicker
                    label="Access End"
                    value={defaultDateEnd}
                    onChange={input.onChange}
                    disablePast
                  />
                )}
              </Field>
            </div>
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} sm={12}>
          <Field
            component={SelectAutoComplete}
            name="vehicles"
            label="Vehicles"
            options={options}
            getOptionLabel={getOptionLabel}
            handleChange={handleChange}
          />
        </Grid>

        <Grid item xs={12}>
          <Field
            component={SwitchField}
            name="allowTrackingHistory"
            type="checkbox"
            label="Allow tracking history"
          />
        </Grid>

        <Grid item xs={12}>
          <Field
            component={SwitchField}
            name="shareLiveCameras"
            type="checkbox"
            label="Share live cameras"
          />
        </Grid>

        <FormSpy subscription={{ values: true }}>
          {({ values }) =>
            values.shareLiveCameras ? (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Included live cameras
                </Typography>
                {cameraUnion.length === 0 ? (
                  <FormHelperText>
                    Selected vehicles have no cameras
                  </FormHelperText>
                ) : (
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Cameras</FormLabel>
                    <FormGroup row>
                      {cameraUnion.map((location) => (
                        <Field
                          key={location}
                          name="cameraPositions"
                          type="checkbox"
                          value={location}
                        >
                          {({ input }) => (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={Boolean(input.checked)}
                                  onChange={input.onChange}
                                  name={input.name}
                                  value={input.value}
                                />
                              }
                              label={mapToDisplay(location)}
                            />
                          )}
                        </Field>
                      ))}
                    </FormGroup>
                  </FormControl>
                )}
              </Grid>
            ) : null
          }
        </FormSpy>
      </Grid>
    </div>
  );
};

export default CreateLiveShareTrackingForm;
